"use client";

import { AlertTriangle, Box, CheckCircle2, Code2, ExternalLink, Play, ShieldAlert } from "lucide-react";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/components/auth/auth-provider";
import { SiteNav } from "@/components/layout/site-nav";
import { siteConfigRepository } from "@/repositories/site-config-repository";
import {
  DEFAULT_YOUTUBE_VIDEO_ID,
  YOUTUBE_VIDEO_MESH_SIZE,
  YOUTUBE_VIDEO_PIXEL_SIZE,
  buildYouTubeEmbedUrl,
  resolveYouTubeVideoId,
} from "@/lib/youtube-embed";
import {
  YOUTUBE_DEBUG_STEPS,
  getYouTubeDebugPath,
  type YouTubeDebugStep,
} from "@/lib/youtube-debug";
import { cn } from "@/lib/utils";

type DebugVariant = "plain" | "css-transform" | "babylon-overlay" | "babylon-html-mesh";

const VARIANT_BY_STEP: Record<YouTubeDebugStep["number"], DebugVariant> = {
  "01": "plain",
  "02": "css-transform",
  "03": "babylon-overlay",
  "04": "babylon-html-mesh",
};

export function YouTubeDebugPage({ stepNumber }: { stepNumber: YouTubeDebugStep["number"] }) {
  const auth = useAuth();
  const config = siteConfigRepository.getSiteConfig();
  const step = YOUTUBE_DEBUG_STEPS.find((candidate) => candidate.number === stepNumber) ?? YOUTUBE_DEBUG_STEPS[0];

  return (
    <>
      <SiteNav brand={config.brand} items={config.navigation} developerItems={config.developerNavigation} />
      {auth.hasRole("developer") ? (
        <DeveloperYouTubeDiagnostic step={step} variant={VARIANT_BY_STEP[step.number]} />
      ) : (
        <DeveloperAccessRequired />
      )}
    </>
  );
}

function DeveloperAccessRequired() {
  return (
    <main className="grid min-h-screen place-items-center bg-background px-6 pt-16">
      <div className="max-w-md text-center">
        <ShieldAlert className="mx-auto h-9 w-9 text-red-400" aria-hidden />
        <h1 className="mt-4 text-2xl font-semibold">Developer access required</h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          These YouTube/Babylon diagnostics require the exact <code>developer</code> claim in a valid Synaptic JWT.
        </p>
      </div>
    </main>
  );
}

function DeveloperYouTubeDiagnostic({
  step,
  variant,
}: {
  step: YouTubeDebugStep;
  variant: DebugVariant;
}) {
  const [searchParams] = useSearchParams();
  const requestedVideo = searchParams.get("youtube");
  const videoId = resolveYouTubeVideoId(requestedVideo);
  const embedUrl = buildYouTubeEmbedUrl(videoId);

  return (
    <main className="min-h-screen bg-[#02070b] px-4 pb-12 pt-24 sm:px-6">
      <div className="mx-auto max-w-7xl">
        <header className="grid gap-5 border-b border-white/10 pb-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
              <Code2 className="h-4 w-4" aria-hidden />
              Developer-only embed isolation
            </div>
            <h1 className="mt-2 text-3xl font-semibold">
              {step.number}. {step.label}
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">{step.description}</p>
          </div>
          <VideoSelector videoId={videoId} />
        </header>

        <YouTubeDebugNav currentStep={step} videoId={videoId} />

        <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0">
            {variant === "plain" ? <PlainIframe embedUrl={embedUrl} /> : null}
            {variant === "css-transform" ? <CssTransformedIframe embedUrl={embedUrl} /> : null}
            {variant === "babylon-overlay" ? <BabylonOverlayIframe embedUrl={embedUrl} /> : null}
            {variant === "babylon-html-mesh" ? <AnimatedBabylonHtmlMesh embedUrl={embedUrl} /> : null}
          </div>
          <DiagnosticFacts step={step} videoId={videoId} embedUrl={embedUrl} />
        </section>
      </div>
    </main>
  );
}

function YouTubeDebugNav({ currentStep, videoId }: { currentStep: YouTubeDebugStep; videoId: string }) {
  return (
    <nav className="my-6 grid gap-2 md:grid-cols-4" aria-label="YouTube embed debugging sequence">
      {YOUTUBE_DEBUG_STEPS.map((step) => {
        const active = step.number === currentStep.number;
        return (
          <Link
            key={step.number}
            to={getYouTubeDebugPath(step, videoId)}
            aria-current={active ? "page" : undefined}
            className={cn(
              "border px-3 py-3 transition",
              active
                ? "border-cyan-300/60 bg-cyan-300/10 text-cyan-50"
                : "border-white/10 bg-white/[0.025] text-muted-foreground hover:border-white/25 hover:text-foreground",
            )}
          >
            <span className="text-xs font-bold text-cyan-300">{step.number}</span>
            <span className="ml-2 text-sm font-medium">{step.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

function VideoSelector({ videoId }: { videoId: string }) {
  const [value, setValue] = useState(videoId);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => setValue(videoId), [videoId]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const resolved = resolveYouTubeVideoId(value);
    navigate(`${location.pathname}?${new URLSearchParams({ youtube: resolved })}`);
  };

  return (
    <form onSubmit={submit} className="flex w-full max-w-xl gap-2 lg:w-[430px]">
      <label className="sr-only" htmlFor="youtube-debug-source">YouTube video ID or URL</label>
      <input
        id="youtube-debug-source"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={DEFAULT_YOUTUBE_VIDEO_ID}
        className="min-w-0 flex-1 border border-white/15 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-300/60"
      />
      <button type="submit" className="inline-flex items-center gap-2 bg-cyan-300 px-3 py-2 text-sm font-semibold text-black">
        <Play className="h-4 w-4" aria-hidden />
        Load
      </button>
    </form>
  );
}

function PlainIframe({ embedUrl }: { embedUrl: string }) {
  return (
    <DiagnosticViewport label="Plain iframe">
      <YouTubeIframe embedUrl={embedUrl} />
    </DiagnosticViewport>
  );
}

function CssTransformedIframe({ embedUrl }: { embedUrl: string }) {
  return (
    <DiagnosticViewport label="CSS-transformed div">
      <div className="grid h-full place-items-center [perspective:1200px]">
        <div
          className="w-[86%] overflow-hidden border border-cyan-300/40 bg-black shadow-[0_30px_90px_rgba(0,180,255,0.22)]"
          style={{ aspectRatio: "16 / 9", transform: "rotateX(5deg) rotateY(-12deg) scale(0.92)" }}
        >
          <YouTubeIframe embedUrl={embedUrl} />
        </div>
      </div>
    </DiagnosticViewport>
  );
}

function BabylonOverlayIframe({ embedUrl }: { embedUrl: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useBasicBabylonScene(canvasRef);

  return (
    <DiagnosticViewport label="Babylon canvas with ordinary DOM overlay" flush>
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full outline-none" />
      <div className="absolute inset-0 grid place-items-center">
        <div className="w-[72%] overflow-hidden border border-cyan-300/55 bg-black shadow-2xl" style={{ aspectRatio: "16 / 9" }}>
          <YouTubeIframe embedUrl={embedUrl} />
        </div>
      </div>
    </DiagnosticViewport>
  );
}

function AnimatedBabylonHtmlMesh({ embedUrl }: { embedUrl: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const rendererHostId = `youtube-html-mesh-debug-${useId().replace(/:/g, "")}`;
  const [status, setStatus] = useState("Initializing Babylon HtmlMesh");

  useEffect(() => {
    let cancelled = false;
    let engine: import("@babylonjs/core").Engine | null = null;
    let scene: import("@babylonjs/core").Scene | null = null;
    let renderer: import("@babylonjs/addons").HtmlMeshRenderer | null = null;

    async function initialize() {
      if (!canvasRef.current || !contentRef.current) return;
      const [B, addons] = await Promise.all([import("@babylonjs/core"), import("@babylonjs/addons")]);
      if (cancelled || !canvasRef.current || !contentRef.current) return;

      engine = new B.Engine(canvasRef.current, true);
      scene = new B.Scene(engine);
      // In-scene HtmlMesh content is rendered beneath the WebGL canvas. Its
      // depth mask can only reveal that DOM content through a transparent clear.
      scene.clearColor = new B.Color4(0.005, 0.015, 0.03, 0);

      const camera = new B.ArcRotateCamera("debug-camera", -Math.PI / 2, Math.PI / 2.25, 7, B.Vector3.Zero(), scene);
      camera.attachControl(canvasRef.current, true);
      camera.lowerRadiusLimit = 4.5;
      camera.upperRadiusLimit = 10;

      const light = new B.HemisphericLight("debug-light", new B.Vector3(0, 1, -0.5), scene);
      light.intensity = 0.9;

      const frame = B.MeshBuilder.CreateBox(
        "debug-video-frame",
        { width: 4.3, height: 2.55, depth: 0.12 },
        scene,
      );
      const frameMaterial = new B.StandardMaterial("debug-frame-material", scene);
      frameMaterial.diffuseColor = new B.Color3(0.015, 0.11, 0.16);
      frameMaterial.emissiveColor = new B.Color3(0.01, 0.08, 0.12);
      frame.material = frameMaterial;
      frame.position.z = 0.08;

      renderer = new addons.HtmlMeshRenderer(scene, {
        parentContainerId: rendererHostId,
      });
      const htmlMesh = new addons.HtmlMesh(scene, "debug-youtube-plane", {
        captureOnPointerEnter: true,
        fitStrategy: addons.FitStrategy.CONTAIN,
      });
      contentRef.current.style.visibility = "visible";
      htmlMesh.setContent(
        contentRef.current,
        YOUTUBE_VIDEO_MESH_SIZE.width,
        YOUTUBE_VIDEO_MESH_SIZE.height,
      );
      htmlMesh.isPickable = true;

      scene.onBeforeRenderObservable.add(() => {
        const seconds = performance.now() / 1000;
        htmlMesh.rotation.y = Math.sin(seconds * 0.55) * 0.32;
        htmlMesh.rotation.x = Math.sin(seconds * 0.37) * 0.08;
        htmlMesh.position.y = Math.sin(seconds * 0.8) * 0.24;
        frame.rotation.copyFrom(htmlMesh.rotation);
        frame.position.y = htmlMesh.position.y;
      });

      engine.runRenderLoop(() => scene?.render());
      window.addEventListener("resize", resize);
      setStatus("HtmlMesh active — drag outside the video to orbit");
    }

    const resize = () => engine?.resize();
    void initialize().catch((error: unknown) => {
      if (!cancelled) setStatus(error instanceof Error ? error.message : "Babylon initialization failed");
    });

    return () => {
      cancelled = true;
      window.removeEventListener("resize", resize);
      renderer?.dispose();
      scene?.dispose();
      engine?.dispose();
    };
  }, [rendererHostId]);

  return (
    <DiagnosticViewport label="Animated Babylon HtmlMesh" flush>
      <div id={rendererHostId} className="isolate absolute inset-0 z-0 overflow-hidden">
        <canvas ref={canvasRef} className="absolute inset-0 h-full w-full outline-none" />
        <div
          ref={contentRef}
          data-testid="debug-youtube-html-mesh-content"
          style={{
            // HtmlMesh's CONTAIN wrapper must be able to measure this node in
            // normal flow. Absolute positioning would place its top-left at the
            // mesh origin and exclude its 1024x576 box from flex centering.
            position: "relative",
            width: `${YOUTUBE_VIDEO_PIXEL_SIZE.width}px`,
            height: `${YOUTUBE_VIDEO_PIXEL_SIZE.height}px`,
            background: "#000",
            pointerEvents: "auto",
            visibility: "hidden",
          }}
        >
          <YouTubeIframe embedUrl={embedUrl} />
        </div>
      </div>
      <div className="pointer-events-none absolute bottom-3 left-3 border border-white/10 bg-black/70 px-3 py-2 text-xs text-cyan-100">
        {status}
      </div>
    </DiagnosticViewport>
  );
}

function DiagnosticViewport({
  children,
  label,
  flush = false,
}: {
  children: ReactNode;
  label: string;
  flush?: boolean;
}) {
  return (
    <section className="overflow-hidden border border-white/10 bg-black/45">
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
        <span className="inline-flex items-center gap-1.5 text-xs text-cyan-300">
          <Box className="h-3.5 w-3.5" aria-hidden />
          16:9
        </span>
      </div>
      <div className={cn("relative aspect-video min-h-[360px]", !flush && "p-5")}>{children}</div>
    </section>
  );
}

function YouTubeIframe({ embedUrl }: { embedUrl: string }) {
  return (
    <iframe
      src={embedUrl}
      title="YouTube debugging stream"
      width="100%"
      height="100%"
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      loading="eager"
      referrerPolicy="strict-origin-when-cross-origin"
      style={{ border: 0, display: "block" }}
    />
  );
}

function DiagnosticFacts({
  step,
  videoId,
  embedUrl,
}: {
  step: YouTubeDebugStep;
  videoId: string;
  embedUrl: string;
}) {
  const facts = useMemo(() => {
    const completed = Number(step.number);
    return YOUTUBE_DEBUG_STEPS.map((candidate) => ({
      ...candidate,
      reached: Number(candidate.number) <= completed,
    }));
  }, [step.number]);

  return (
    <aside className="space-y-4">
      <section className="border border-white/10 bg-white/[0.025] p-4">
        <h2 className="text-sm font-semibold">Current source</h2>
        <dl className="mt-3 grid gap-3 text-xs">
          <div>
            <dt className="text-muted-foreground">Video ID</dt>
            <dd className="mt-1 break-all font-mono text-cyan-100">{videoId}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Iframe URL</dt>
            <dd className="mt-1 break-all font-mono leading-5 text-cyan-100">{embedUrl}</dd>
          </div>
        </dl>
        <a
          href={`https://www.youtube.com/watch?v=${videoId}`}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-2 text-xs text-cyan-300 hover:text-cyan-100"
        >
          Open source on YouTube
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      </section>

      <section className="border border-white/10 bg-white/[0.025] p-4">
        <h2 className="text-sm font-semibold">Isolation ladder</h2>
        <ol className="mt-3 grid gap-3">
          {facts.map((fact) => (
            <li key={fact.number} className="flex gap-2 text-xs">
              {fact.reached ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
              ) : (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              )}
              <div>
                <p className={fact.reached ? "text-foreground" : "text-muted-foreground"}>
                  {fact.number}. {fact.label}
                </p>
                <p className="mt-0.5 leading-5 text-muted-foreground">{fact.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </aside>
  );
}

function useBasicBabylonScene(canvasRef: React.RefObject<HTMLCanvasElement | null>) {
  useEffect(() => {
    let cancelled = false;
    let engine: import("@babylonjs/core").Engine | null = null;
    let scene: import("@babylonjs/core").Scene | null = null;

    async function initialize() {
      if (!canvasRef.current) return;
      const B = await import("@babylonjs/core");
      if (cancelled || !canvasRef.current) return;

      engine = new B.Engine(canvasRef.current, true);
      scene = new B.Scene(engine);
      scene.clearColor = new B.Color4(0.005, 0.02, 0.035, 1);

      const camera = new B.ArcRotateCamera("debug-overlay-camera", -Math.PI / 2, Math.PI / 2.4, 7, B.Vector3.Zero(), scene);
      camera.attachControl(canvasRef.current, true);

      const light = new B.HemisphericLight("debug-overlay-light", new B.Vector3(0.2, 1, -0.3), scene);
      light.intensity = 0.9;

      const sphere = B.MeshBuilder.CreateSphere("debug-sphere", { diameter: 2.2, segments: 24 }, scene);
      sphere.position.x = -2.6;
      const material = new B.StandardMaterial("debug-sphere-material", scene);
      material.wireframe = true;
      material.emissiveColor = new B.Color3(0.02, 0.45, 0.7);
      sphere.material = material;

      scene.onBeforeRenderObservable.add(() => {
        sphere.rotation.y += 0.004;
      });

      engine.runRenderLoop(() => scene?.render());
      window.addEventListener("resize", resize);
    }

    const resize = () => engine?.resize();
    void initialize();

    return () => {
      cancelled = true;
      window.removeEventListener("resize", resize);
      scene?.dispose();
      engine?.dispose();
    };
  }, [canvasRef]);
}
